import { useState, useCallback } from "react";
import {
	Box,
	Typography,
	TextField,
	Link,
	Button,
	CircularProgress,
	Divider,
	OutlinedInput,
	InputAdornment,
	IconButton,
	FormControlLabel,
	Checkbox,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import makeStyles from "@mui/styles/makeStyles";
import { styled } from "@mui/material/styles";

import { PersonalInfoBlock, FieldContainer } from "./PersonalInfoBlock";
import { useStateContext } from "../../../context/StateContext";
import { FieldError } from "../../../components";
import checkPasswordStrength from "../../../hooks/checkPasswordStrength";
import { MAX_PASSWORD_STRENGTH } from "../../../const";

import { PolicyAndTermsBlock } from "../PolicyAndTermsBlock";

const useStyles = makeStyles((theme) => ({
	heading: {
		fontWeight: 600,
		fontSize: "20px",
		lineHeight: "24px",
		paddingBottom: "20px",
	},
	wrapper: {
		position: "relative",
		width: "fit-content",
	},
}));

const SignUpContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	padding: "25px",
	background: "#FFFFFF",
	border: "1px solid #DADADA",
	borderRadius: "4px",
	gap: "30px",
	maxWidth: "400px",
}));

const FormContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexWrap: "wrap",
	gap: "15px 20px",
	justifyContent: "space-between",
}));

const CustomCircularProgress = styled(CircularProgress)(({ theme }) => ({
	color: theme.palette.primary.main,
	position: "absolute",
	top: "50%",
	left: "50%",
	marginTop: -4,
	marginLeft: -15,
}));

const Header = styled(Typography)(({ theme }) => ({
	fontSize: "30px",
	fontWeight: 600,
	lineHeight: "normal",
}));
const SubHeader = styled(Typography)(({ theme }) => ({
	fontSize: "20px",
	fontWeight: 400,
	lineHeight: "30px",
}));

function SignUpBlock({
	userObj,
	handleUserObjChange,
	loading,
	handleSubmit,
	errorMessage,
	setPasswordValidation,
}) {
	const { email, password, first_name, last_name, toc, phone } = userObj;

	const classes = useStyles();
	const { dispatch } = useStateContext();
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordAdvice, setShowPasswordAdvice] = useState(false);

	const handleClickShowPassword = useCallback(
		() => setShowPassword((show) => !show),
		[]
	);

	const handleMouseDownPassword = useCallback((event) => {
		event.preventDefault();
	}, []);

	const handleHitEnter = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleSubmit("SIGN_UP");
				e.preventDefault();
			}
		},
		[handleSubmit]
	);

	const renderPasswordError = useCallback(() => {
		if (showPasswordAdvice) {
			return <FieldError field="password" styleType="info" />;
		}

		if (password.showError) {
			return <FieldError field="password" />;
		}
	}, [password, showPasswordAdvice]);

	const onKeyDown = useCallback((e) => handleHitEnter(e), [handleHitEnter]);

	const handleTocChange = useCallback(
		(e) => {
			handleUserObjChange(e.target.checked, "toc");
		},
		[handleUserObjChange]
	);

	return (
		<SignUpContainer>
			<Box>
				<Header variant="h1">{"Create your new 3T account"}</Header>
				<SubHeader>
					{
						"Activate Studio 3T, manage your license, and access all services from 3T Software Labs."
					}
				</SubHeader>
			</Box>

			<FormContainer>
				<FieldContainer>
					<Typography>Email *</Typography>
					<TextField
						fullWidth
						autoComplete="email"
						variant="outlined"
						size="small"
						error={email.showError}
						value={email.value}
						onKeyDown={onKeyDown}
						onChange={(e) =>
							handleUserObjChange(e.currentTarget.value, "email")
						}
						type="email"
					/>
					{email.showError && <FieldError field="email" />}
				</FieldContainer>
				<FieldContainer>
					<Typography>Password *</Typography>
					<Box sx={{ position: "relative" }}>
						<OutlinedInput
							fullWidth
							autoComplete="new-password"
							type={showPassword ? "text" : "password"}
							size="small"
							error={password.showError}
							onBlur={() => {
								setShowPasswordAdvice(false);
								setPasswordValidation(
									checkPasswordStrength(password.value) !==
										MAX_PASSWORD_STRENGTH
								);
							}}
							onFocus={() => setShowPasswordAdvice(true)}
							onKeyDown={onKeyDown}
							value={password.value}
							onChange={(e) => {
								handleUserObjChange(
									e.currentTarget.value,
									"password"
								);
							}}
							endAdornment={
								<InputAdornment position="end">
									<IconButton
										onClick={handleClickShowPassword}
										onMouseDown={handleMouseDownPassword}
										edge="end"
									>
										{showPassword ? (
											<VisibilityOff />
										) : (
											<Visibility />
										)}
									</IconButton>
								</InputAdornment>
							}
						/>
						{renderPasswordError()}
					</Box>
				</FieldContainer>

				<PersonalInfoBlock
					onKeyDown={onKeyDown}
					userObj={userObj}
					handleUserObjChange={handleUserObjChange}
				/>
			</FormContainer>

			<Box>
				<Box>
					<FormControlLabel
						control={
							<Checkbox
								checked={toc.value || false}
								onChange={handleTocChange}
								inputProps={{ "aria-label": "toc" }}
							/>
						}
						label={<PolicyAndTermsBlock />}
					/>
					{toc.showError && <FieldError field="toc" />}
				</Box>
				<Box>
					{errorMessage && (
						<FieldError
							field="sign_up_error"
							customMessage={errorMessage}
						/>
					)}
				</Box>

				<div className={classes.wrapper} key={"sign_up"}>
					<Button
						disabled={loading}
						sx={{
							marginTop: "15px",
						}}
						variant={"contained"}
						onClick={() => {
							handleSubmit("SIGN_UP");
						}}
					>
						Sign up
					</Button>
					{loading && <CustomCircularProgress size={24} />}
				</div>
			</Box>
			<Divider flexItem />
			<Typography>
				Already have an account?{" "}
				<Link
					onClick={() =>
						dispatch({
							type: "SET_VIEW",
							payload: "SIGN_IN",
						})
					}
				>
					Sign in
				</Link>
			</Typography>
		</SignUpContainer>
	);
}

export default SignUpBlock;
